import { AxiosError, AxiosResponse } from 'axios';
import { ORG_API_TIMEOUT, REVIEW_SERVICE_URL, TBID_URL } from '~/modules/config';
import { AuthTimeoutError } from '~/modules/errors/authErrors';
import { CreateReviewError, DeleteReviewError, EditReviewError } from '~/modules/errors/reviewErrors';
import { BadgeVariant, generateLocalizedBadge, generateSimpleBadge } from '~/modules/helpers/badgeHelper';
import { generateBaseUrl } from '~/modules/helpers/routingHelper';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { setupOrgRequestConfig } from '~/modules/utilities/httpUtilities';
import { printDateSimple } from '~/modules/utilities/time';
import { Comment, ListingReviews, Review, Reviewer, UserReview } from '../stores/reviewStore';

export interface ReviewUser {
    id: string;
    tbidUserId?: string;
    name: string;
    firstName: string;
    lastName: string;
    vanity?: string;
    userPicture?: string;
    isSalesforceMVP?: boolean;
    isTopContributor?: boolean;
    trailheadRank?: string;
}

export interface CommentResponseModel {
    id: string;
    type: string;
    questionnaireResponseId: string;
    body: string;
    commentDate: string;
    user: ReviewUser;
}

export interface QuestionResponse {
    questionId: string;
    response: string;
    questionName: string;
    questionGroup: string;
    description: string;
}

export interface ReviewResponseModel {
    id: string;
    type: string;
    reviewDate: string;
    rating: number;
    listingId: string;
    likeCount: number;
    userLikeId: string;
    questionResponses: QuestionResponse[];
    comments: CommentResponseModel[];
    user: ReviewUser;
}

export interface UserQuestionResponse extends QuestionResponse {
    questionResponseId: string; //this will have the value of "null" prior to initial saving of review
    sortOrder: number;
    isRequired: boolean;
    dataType: string;
    allowedResponses: string;
}

export interface UserReviewResponseModel {
    questionnaireResponseId: string; //this will have the value of "null" prior to initial saving of review
    questionnaireId: string;
    listingId: string;
    questionResponses: UserQuestionResponse[];
}

export interface ReviewResponse {
    reviews: ReviewResponseModel[];
    hasNext?: boolean;
}

export interface ReviewSummary {
    reviewCount: number;
    averageRating: number;
}

export const REPLY_TYPES = {
    Comment: 'Comment',
    Helpful: 'Helpful',
    'Report Abuse': 'Report Abuse',
} as const;

export interface NewReply {
    type: keyof typeof REPLY_TYPES;
    questionnaireResponseId: string;
    body: string;
}

export enum LikeStatus {
    liked = 'LIKED',
    inProgress = 'IN_PROGRESS',
    unliked = 'UNLIKED',
}

export enum CommentStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    AVAILABLE = 'AVAILABLE',
}

export enum WriteReviewStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    AVAILABLE = 'AVAILABLE',
}

export enum DeleteReviewStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    AVAILABLE = 'AVAILABLE',
}

export enum SortBy {
    RECENT = 'mr',
    FAVORABLE = 'mf',
    HELPFUL = 'mh',
}

export enum LoadingStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    LOADING_MORE = 'LOADING_MORE',
    FILTERING = 'FILTERING',
    SORTING = 'SORTING',
    ERROR = 'ERROR',
}

const PARAM_LISTING_ID = 'listingId';
const PARAM_PAGE_LENGTH = 'pageLength';
const PARAM_PAGE_NUMBER = 'pageNumber';
const PARAM_USER_ID = 'userId';
const PARAM_FILTERS = 'filters';
const PARAM_SORT = 'sort';
const PARAM_SUMMARY = 'summary';
const DEFAULT_TRAILBLAZER_PROFILE_PICTURE_PATTERN = 'profilephoto/005/';
const DEFAULT_TBID_PROFILE_PICTURE_PATTERN = 'default_profile';
const DEFAULT_PROFILE_PICTURE_PATTERNS = [
    DEFAULT_TBID_PROFILE_PICTURE_PATTERN,
    DEFAULT_TRAILBLAZER_PROFILE_PICTURE_PATTERN,
];

export class ReviewService {
    get endpoint(): string {
        return REVIEW_SERVICE_URL;
    }

    async getReview(reviewId: string): Promise<Review> {
        const url = `${this.endpoint}/reviews/${reviewId}`;
        try {
            const httpConfig: HttpRequestConfig = setupOrgRequestConfig({ includeAuth: false });
            const response = await http.get(url, httpConfig);
            const reviews = this.buildReviews(response.data.reviews);
            return reviews[0];
        } catch (error) {
            handleException(error, 'reviewService.getReview');
            return Promise.reject(error);
        }
    }

    async getReviewForUser(listingId: string, userId: string): Promise<UserReview> {
        const url = `${this.endpoint}/reviews?${PARAM_LISTING_ID}=${listingId}&${PARAM_USER_ID}=${userId}`;

        let userReview: UserReview;
        try {
            const httpConfig: HttpRequestConfig = setupOrgRequestConfig();
            const response = await http.get(url, httpConfig);
            userReview = this.buildUserReview(response.data, userId);
        } catch (err) {
            handleException(err, 'reviewService.getReviewForUser');
            return Promise.reject(err);
        }
        return userReview;
    }

    async getReviews(
        listingId: string,
        pageLength?: number,
        pageNumber?: number,
        filters?: string,
        sort?: string,
    ): Promise<ListingReviews> {
        let url = `${this.endpoint}/reviews?${PARAM_LISTING_ID}=${listingId}`;
        if (pageLength) {
            url += `&${PARAM_PAGE_LENGTH}=${pageLength}`;
        }
        if (pageNumber) {
            url += `&${PARAM_PAGE_NUMBER}=${pageNumber}`;
        }
        if (filters) {
            url += `&${PARAM_FILTERS}=${filters}`;
        }
        if (sort) {
            url += `&${PARAM_SORT}=${sort}`;
        }
        return this.doGetReviews(url);
    }

    async doGetReviews(url: string): Promise<ListingReviews> {
        try {
            const httpConfig: HttpRequestConfig = setupOrgRequestConfig({ includeAuth: false });
            const response = await http.get(url, httpConfig);
            const reviews = this.buildReviews(response.data.reviews);
            return { reviews: reviews, hasNext: response.data.hasNext, total: response.data.totalReviewCount };
        } catch (error) {
            if ((error as AxiosError)?.response?.status === 404) {
                return { reviews: [], hasNext: false, total: 0 };
            }
            handleException(error, 'reviewService.doGetReviews');
            return Promise.reject(error);
        }
    }

    async unlikeReview(reviewId: string, replyId: string): Promise<Review> {
        const httpConfig: HttpRequestConfig = setupOrgRequestConfig();
        const url = `${this.endpoint}/reviews/${reviewId}/replies/${replyId}`;
        try {
            const response = await http.delete(url, httpConfig);
            const reviews = this.buildReviews(response.data.reviews);
            return reviews[0];
        } catch (error) {
            handleException(error, 'reviewService.unlikeReview', {
                responseCode: error?.response?.status ?? -1,
            });
            if ((error as AxiosError)?.response?.status === 403) {
                return Promise.reject(
                    new AuthTimeoutError(
                        `Auth token has expired. Failed to unlike review. URL: ${JSON.stringify(url)}`,
                    ),
                );
            }
            return Promise.reject(error);
        }
    }

    async createReply(reviewId: string, comment: string, type: NewReply['type']): Promise<Review> {
        const body: NewReply = {
            type: type,
            questionnaireResponseId: reviewId,
            body: comment,
        };
        try {
            const request: HttpRequestConfig = setupOrgRequestConfig();
            const url = `${this.endpoint}/reviews/${reviewId}/replies`;

            request.data = body;
            const response: AxiosResponse<ReviewResponse> = await http.post(url, request);
            const reviews = this.buildReviews(response.data.reviews);
            return reviews[0];
        } catch (error) {
            handleException(error, 'reviewService.createReply', {
                responseCode: error?.response?.status ?? -1,
            });
            if ((error as AxiosError)?.response?.status === 403) {
                return Promise.reject(
                    new AuthTimeoutError(`Auth token has expired. Failed to create reply ${JSON.stringify(body)}`),
                );
            }
            return Promise.reject(error);
        }
    }

    async createReview(userReview: UserReview): Promise<UserReview> {
        let userReviewUpdated: UserReview;
        try {
            const request: HttpRequestConfig = setupOrgRequestConfig();
            const url = `${this.endpoint}/reviews`;

            request.data = userReview;

            const response = await http.post(url, request);
            userReviewUpdated = this.buildUserReview(response.data, userReview.userId);
        } catch (error) {
            handleException(error, 'reviewService.createReview', {
                responseCode: error?.response?.status ?? -1,
            });
            if ((error as AxiosError)?.response?.status === 403) {
                return Promise.reject(
                    new AuthTimeoutError(
                        `Auth token has expired. Failed to create review ${JSON.stringify(userReview)}`,
                    ),
                );
            }
            return Promise.reject(new CreateReviewError(`Failed to create review ${JSON.stringify(userReview)}`));
        }
        return userReviewUpdated;
    }

    async editReview(userReview: UserReview): Promise<UserReview> {
        try {
            const request: HttpRequestConfig = setupOrgRequestConfig();
            const url = `${this.endpoint}/reviews/${userReview.questionnaireResponseId}`;
            //TODO: Discuss the possibility of adding different timeouts per environment. QA takes a bit of time for reviews on some cases
            request.timeout = ORG_API_TIMEOUT;
            request.data = userReview;
            const response = await http.post(url, request);
            return this.buildUserReview(response.data, userReview.userId);
        } catch (error) {
            handleException(error, 'reviewService.editReview', {
                responseCode: error?.response?.status ?? -1,
            });

            if ((error as AxiosError)?.response?.status === 403) {
                return Promise.reject(
                    new AuthTimeoutError(`Auth token has expired. Failed to edit review ${JSON.stringify(userReview)}`),
                );
            }
            return Promise.reject(new EditReviewError(`Failed to edit review ${JSON.stringify(userReview)}`));
        }
    }

    async deleteReview(reviewId: string): Promise<void> {
        try {
            const request: HttpRequestConfig = setupOrgRequestConfig();
            const url = `${this.endpoint}/reviews/${reviewId}`;
            await http.delete(url, request);
            return Promise.resolve();
        } catch (error) {
            handleException(error, 'reviewService.deleteReview', {
                responseCode: error?.response?.status ?? -1,
            });
            if ((error as AxiosError)?.response?.status === 403) {
                return Promise.reject(
                    new AuthTimeoutError(
                        `Auth token has expired. Failed to delete reviewId ${JSON.stringify(reviewId)}`,
                    ),
                );
            }
            return Promise.reject(new DeleteReviewError(`Failed to delete reviewId ${reviewId}`));
        }
    }

    async getSummary(listingId: string): Promise<ReviewSummary> {
        const request: HttpRequestConfig = setupOrgRequestConfig({ includeAuth: false });
        const url = `${this.endpoint}/reviews?${PARAM_LISTING_ID}=${listingId}&${PARAM_SUMMARY}`;

        const response = await http.get(url, request);
        return { reviewCount: response.data.reviewCount, averageRating: response.data.averageRating };
    }

    private buildComments(rawComments: CommentResponseModel[], userCache: Record<string, Reviewer>): Comment[] {
        const comments: Comment[] = rawComments.map((comment) => {
            const content = comment.body;
            const commentDate = printDateSimple(comment.commentDate);
            const user = this.buildUser(comment.user, userCache);

            return {
                id: comment.id,
                user: user,
                date: commentDate,
                content: content,
                reviewId: comment.questionnaireResponseId,
            };
        });

        return comments;
    }

    private buildReviews(rawReviews: ReviewResponseModel[]): Review[] {
        const userCache: Record<string, Reviewer> = {};

        const reviews: Review[] = rawReviews.map((review) => {
            const titleResponse = review.questionResponses.filter((question) => question.questionName === 'Title')[0];
            const title = titleResponse?.response || '';

            const commentsResponse = review.questionResponses.filter(
                (question) => question.questionName === 'Comments',
            )[0];
            const content = commentsResponse?.response || '';

            const comments = review.comments ? this.buildComments(review.comments, userCache) : [];
            const reviewDate = printDateSimple(review.reviewDate);
            const likeStatus = review.userLikeId ? LikeStatus.liked : LikeStatus.unliked;

            const user = this.buildUser(review.user, userCache);
            return {
                id: review.id,
                user: user,
                rating: review.rating,
                date: reviewDate,
                title: title,
                content: content,
                userLikeId: review.userLikeId,
                likeCount: review.likeCount,
                likeStatus: likeStatus,
                comments: comments,
                commentStatus: CommentStatus.AVAILABLE,
                commentBoxShown: false,
            };
        });
        return reviews;
    }

    private buildUser(user: ReviewUser, userCache: Record<string, Reviewer>): Reviewer {
        if (userCache[user.id]) {
            return userCache[user.id];
        }

        const badges = [];
        if (user.trailheadRank) {
            badges.push(generateSimpleBadge(user.trailheadRank, BadgeVariant.Blue));
        }
        if (user.isSalesforceMVP) {
            badges.push(generateLocalizedBadge('salesforceMVP'));
        }
        if (user.isTopContributor) {
            badges.push(generateLocalizedBadge('topContributor'));
        }

        const avatar = this.buildAvatar(user);

        const profileLink = user.tbidUserId ? `${TBID_URL}/id/${user.vanity}` : null;

        const newUser = {
            tbid: user.tbidUserId,
            name: user.name,
            firstName: user.firstName,
            lastName: user.lastName,
            badges: badges,
            avatar: avatar,
            profileLink: profileLink,
        };
        // @ts-expect-error * Automated Type Error Fix
        userCache[user.id] = newUser;

        // @ts-expect-error * Automated Type Error Fix
        return newUser;
    }

    private buildUserReview(rawReview: UserReviewResponseModel, userId: string): UserReview {
        return {
            questionResponses: rawReview.questionResponses,
            questionnaireResponseId: rawReview.questionnaireResponseId,
            questionnaireId: rawReview.questionnaireId,
            listingId: rawReview.listingId,
            userId,
        };
    }

    private buildAvatar(user: ReviewUser): string {
        // @ts-expect-error * Automated Type Error Fix
        if (DEFAULT_PROFILE_PICTURE_PATTERNS.some((pattern) => user.userPicture.includes(pattern))) {
            // @ts-expect-error * Automated Type Error Fix
            return null;
        }

        if (user.tbidUserId) {
            // @ts-expect-error * Automated Type Error Fix
            return user.userPicture;
        }

        return `${generateBaseUrl()}${user.userPicture}`;
    }
}
